import React, { useState, useEffect } from 'react'
import { XIcon } from '@heroicons/react/solid'

import TagAutocomplete from '@/shared/TagAutocomplete'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import Card from '@/shared/Card'

// Tags and equipment were two copy-pasted sections hitting two endpoints.
// One config drives both now. `source` names which endpoint returns the kind.
const TAG_KINDS = [
  { key: 'genderAges', kind: 'user-gender-age', label: 'Voice age and gender', placeholder: 'Female young adult, male teen', source: 'user_labels', voice: true },
  { key: 'accents', kind: 'user-accents', label: 'Accents', placeholder: 'Texan, Irish, French', source: 'user_labels', voice: true },
  { key: 'languages', kind: 'user-languages', label: 'Languages', placeholder: 'English, Japanese', source: 'user_labels' },
  { key: 'skills', kind: 'user-skills', label: 'Skills', placeholder: 'Character voices, ADR, Figma', source: 'user_labels' },
  { key: 'interests', kind: 'user-interests', label: 'Interests', placeholder: 'Anime, audiobooks, horror', source: 'user_labels' },
  { key: 'softwares', kind: 'user-softwares', label: 'Software', placeholder: 'Audacity, Reaper, Source Connect', source: 'equipments' },
  { key: 'hardwares', kind: 'user-hardwares', label: 'Hardware', placeholder: 'Shure SM7b, Scarlett 2i2', source: 'equipments' }
]
const KIND_TO_KEY = Object.fromEntries(TAG_KINDS.map((k) => [k.kind, k.key]))

const ProfileTags = () => {
  const { getRequest, putpostRequest } = useQuery()
  const [currentUser] = useGlobalState('currentUser')
  const [user, setUser] = useGlobalState('userContainerUser')
  const [tags, setTags] = useState(Object.fromEntries(TAG_KINDS.map((k) => [k.key, []])))

  useEffect(() => {
    ['user_labels', 'equipments'].forEach((source) => {
      getRequest(`/api/v3/labelings/${source}`, {}, (err, jsonData) => {
        if (err) { return }
        setTags((t) => ({ ...t, ...Object.fromEntries(TAG_KINDS.filter((k) => k.source === source).map((k) => [k.key, jsonData[k.key] || []])) }))
      })
    })
  }, [])

  const bump = (delta) => setUser({ ...user, labelingsCount: Math.max(0, (user.labelingsCount || 0) + delta) })

  const addLabel = (label, labelKind) => {
    const key = KIND_TO_KEY[labelKind]
    putpostRequest('/api/v3/labelings', 'POST', { labeling: { labeled_kind: 'User', labeled_id: currentUser.id }, label: { name: label, label_kind: labelKind } }, (err, jsonData) => {
      if (err) { return }
      setTags((t) => ({ ...t, [key]: [...t[key], jsonData.labeling] }))
      bump(1)
    })
  }

  const removeLabel = (labelingId, key) => {
    setTags((t) => ({ ...t, [key]: t[key].filter((l) => l.id !== labelingId) }))
    bump(-1)
    putpostRequest(`/api/v3/labelings/${labelingId}`, 'DELETE', {}, () => {})
  }

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Tags</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>These are what talent search filters on. Skip anything you don't want to be filtered by.</p>
    </div>
    <Card>
      <div className='grid gap-5 sm:grid-cols-2'>
        { TAG_KINDS.map((k) => (
          <div key={k.key}>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>{k.label}{ k.voice && <span className='ml-1 text-xs font-normal text-gray-400'>voice, singing, acting</span> }</label>
            <TagAutocomplete kind={k.kind} addLabel={addLabel} placeholder={k.placeholder} />
            { tags[k.key].length > 0 && <div className='mt-2 flex flex-wrap gap-1'>
              { tags[k.key].map((labeling) => (
                <button key={labeling.id} type='button' onClick={() => removeLabel(labeling.id, k.key)} title='Remove'
                  className='inline-flex items-center gap-x-1 rounded-full bg-cccblue px-2.5 py-0.5 text-xs font-semibold capitalize text-white hover:bg-cccblue-alt'>
                  {labeling.name} <XIcon className='h-3.5 w-3.5' />
                </button>
              ))}
            </div> }
          </div>
        ))}
      </div>
    </Card>
  </div>
}

export default ProfileTags
